import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Header } from '../components/landing/Header';
import { Footer } from '../components/landing/Footer';
import { useInView } from '../hooks/useInView';
import { useEffect } from 'react';

const Section = ({ title, children }) => {
  const [ref, isInView] = useInView();

  return (
    <section className="py-[60px] lg:py-[70px]">
      <div
        ref={ref}
        className={`max-w-[1200px] mx-auto px-6 lg:px-8 ${
          isInView ? 'animate-fade-in-up' : 'opacity-0'
        }`}
      >
        <div className="max-w-[640px]">
          <h2 className="font-heading text-[1.4rem] lg:text-[1.75rem] font-bold text-stone-900 leading-[1.2] tracking-[-0.01em]">
            {title}
          </h2>
          <div className="mt-5">{children}</div>
        </div>
      </div>
    </section>
  );
};

const P = ({ children, className = '' }) => (
  <p className={`text-[0.95rem] text-stone-500 leading-[1.75] mt-3 first:mt-0 ${className}`}>
    {children}
  </p>
);

const BulletList = ({ items }) => (
  <ul className="space-y-2 mt-4 mb-4">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-2.5">
        <span className="mt-[7px] w-[5px] h-[5px] rounded-full bg-stone-400 flex-shrink-0" />
        <span className="text-stone-600 text-[0.9rem] leading-relaxed">{item}</span>
      </li>
    ))}
  </ul>
);

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [heroRef, heroInView] = useInView();

  return (
    <div className="min-h-screen bg-[#faf9f6]">
      <Header />

      <main>
        {/* Hero */}
        <section className="pt-[140px] pb-[50px] lg:pt-[160px] lg:pb-[60px]">
          <div
            ref={heroRef}
            className={`max-w-[1200px] mx-auto px-6 lg:px-8 ${
              heroInView ? 'animate-fade-in-up' : 'opacity-0'
            }`}
          >
            <div className="max-w-[640px]">
              <h1 className="font-heading text-[2.25rem] sm:text-[3rem] lg:text-[3.5rem] leading-[1.08] font-bold text-stone-900 tracking-[-0.02em]">
                About bikes.rentals
              </h1>
              <P className="mt-7 text-[1.05rem]">
                bikes.rentals was created to solve a practical problem: most booking software handles reservations well, but daily rental operations remain chaotic.
              </P>
              <P>
                Bike rental businesses don't just need a calendar. They need clear processes for handovers, deliveries, returns, and issue tracking.
              </P>
              <P>
                bikes.rentals focuses on the operational side of rental businesses — helping operators run smoother days with fewer mistakes and less manual coordination.
              </P>
            </div>
          </div>
        </section>

        <div className="border-t border-stone-200/40" />

        {/* Built from real rental operations */}
        <Section title="Built from real rental operations">
          <P>
            bikes.rentals builds on experience with Click&Share, a self-service rental system developed for automated bike rentals.
          </P>
          <P>
            Working closely with rental operators revealed the same pattern repeatedly:
          </P>
          <P className="text-stone-700 font-medium">
            Booking tools solve the reservation.
          </P>
          <P>
            But everything that happens after the booking still relies on emails, spreadsheets, and manual coordination.
          </P>
          <P>
            bikes.rentals was designed to structure these daily workflows.
          </P>
          <P className="mt-5 text-[0.88rem] text-stone-400">
            Built by the team behind Click&Share — a self-service rental platform used by operators in Europe and the US.
          </P>
        </Section>

        <div className="border-t border-stone-200/40" />

        {/* Operations-first software */}
        <Section title="Operations-first software">
          <P>
            Most rental platforms focus on filling your calendar.
          </P>
          <P className="text-stone-700 font-medium">
            bikes.rentals focuses on running the rental day.
          </P>
          <P>The goal is simple:</P>
          <BulletList
            items={[
              'Fewer customer questions',
              'Clear handovers between staff',
              'Consistent returns and issue documentation',
              'Transparent overview of what is happening today',
            ]}
          />
          <P>
            The result is a calmer rental operation — even as the business grows.
          </P>
        </Section>

        <div className="border-t border-stone-200/40" />

        {/* Who we work with */}
        <Section title="Who we work with">
          <P>
            bikes.rentals is designed for rental operators who run real daily operations — not just a booking calendar.
          </P>
          <P>Typical customers manage:</P>
          <BulletList
            items={[
              'Growing fleets',
              'Multiple staff members',
              'Delivery logistics',
              'Maintenance and issue tracking',
            ]}
          />
          <P>
            bikes.rentals is developed in close collaboration with rental operators, with new features shaped directly by real operational needs.
          </P>
        </Section>

        <div className="border-t border-stone-200/40" />

        {/* Closing line */}
        <section className="py-[60px] lg:py-[70px]">
          <div className="max-w-[1200px] mx-auto px-6 lg:px-8">
            <div className="max-w-[640px]">
              <p className="text-[0.88rem] text-stone-400 mb-2">Our goal is simple:</p>
              <p className="text-[1.1rem] text-stone-800 font-heading font-semibold leading-[1.5]">
                Rental software that helps operators run their business — not just take bookings.
              </p>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="relative py-[90px] lg:py-[110px] bg-[#2d6a5a] overflow-hidden">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIj48ZmlsdGVyIGlkPSJhIj48ZmVUdXJidWxlbmNlIHR5cGU9ImZyYWN0YWxOb2lzZSIgYmFzZUZyZXF1ZW5jeT0iLjc1IiBzdGl0Y2hUaWxlcz0ic3RpdGNoIi8+PC9maWx0ZXI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsdGVyPSJ1cmwoI2EpIiBvcGFjaXR5PSIuMDMiLz48L3N2Zz4=')] opacity-50 pointer-events-none" />
          <div className="max-w-[1200px] mx-auto px-6 lg:px-8 relative z-10 flex flex-col items-center">
            <h2 className="font-heading text-[1.85rem] lg:text-[2.5rem] font-bold text-white leading-[1.15] max-w-[540px] text-center tracking-[-0.01em]">
              See how bikes.rentals works
            </h2>

            <p className="mt-5 text-[1.05rem] text-white/60 leading-[1.7] max-w-[480px] text-center">
              Explore how booking, operations, delivery coordination, and issue tracking work together in one system.
            </p>

            <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
              <Button
                asChild
                className="bg-white text-[#2d6a5a] hover:bg-white/90 rounded-full px-7 h-12 text-[0.92rem] font-medium hover:shadow-lg"
                style={{ transition: 'background-color 0.2s, box-shadow 0.3s' }}
              >
                <Link to="/demo">
                  See the product
                  <ArrowRight className="ml-2" size={17} />
                </Link>
              </Button>

              <Button
                variant="ghost"
                asChild
                className="text-white/60 hover:text-white hover:bg-white/10 rounded-full px-5 h-12 text-[0.88rem] font-medium"
                style={{ transition: 'color 0.2s, background-color 0.2s' }}
              >
                <Link to="/start-setup">Start setup</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
